// Export utilities for Flowise

export const createProjectFile = (projectName, nodes, connections, canvas = {}) => {
  const now = new Date().toISOString();

  return {
    name: projectName,
    version: '1.0',
    app: 'Flowise',
    createdAt: now,
    updatedAt: now,
    canvas: {
      zoom: canvas.zoom || 1,
      panX: canvas.panX || 0,
      panY: canvas.panY || 0
    },
    nodes: nodes,
    connections: connections
  };
};

export const downloadAsJSON = (projectData, filename) => {
  const dataStr = JSON.stringify(projectData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.flowise') ? filename : `${filename}.flowise`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const importFromJSON = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);

        // Validate the file structure
        if (!data.app || data.app !== 'Flowise') {
          reject(new Error('Invalid Flowise project file'));
          return;
        }

        resolve(data);
      } catch (err) {
        reject(new Error('Failed to parse project file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

export const exportAsSVG = (svgElement, filename) => {
  // Clone the SVG to modify it
  const clone = svgElement.cloneNode(true);

  // Get computed styles and inline them
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // Add white background for export
  const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  bg.setAttribute('width', '100%');
  bg.setAttribute('height', '100%');
  bg.setAttribute('fill', '#ffffff');
  clone.insertBefore(bg, clone.firstChild);

  const svgData = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.svg`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportAsPNG = (svgElement, filename, scale = 2) => {
  return new Promise((resolve, reject) => {
    const clone = svgElement.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    // Get dimensions
    const bbox = svgElement.getBoundingClientRect();
    const width = bbox.width * scale;
    const height = bbox.height * scale;

    clone.setAttribute('width', width);
    clone.setAttribute('height', height);

    // Add white background
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#ffffff');
    clone.insertBefore(bg, clone.firstChild);

    const svgData = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pngUrl;
        link.download = `${filename}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pngUrl);
        URL.revokeObjectURL(url);
        resolve();
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to export PNG'));
    };

    img.src = url;
  });
};

// LocalStorage utilities
const STORAGE_KEY = 'flowise_autosave';
const RECENT_PROJECTS_KEY = 'flowise_recent';

export const saveToLocalStorage = (projectData) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...projectData,
      updatedAt: new Date().toISOString()
    }));
    return true;
  } catch (err) {
    console.error('Failed to save to localStorage:', err);
    return false;
  }
};

export const loadFromLocalStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    console.error('Failed to load from localStorage:', err);
    return null;
  }
};

export const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (err) {
    return false;
  }
};

export const addToRecentProjects = (project) => {
  try {
    const recent = getRecentProjects();
    const newEntry = {
      name: project.name,
      updatedAt: project.updatedAt || new Date().toISOString(),
      nodeCount: project.nodes?.length || 0
    };

    // Remove duplicate if exists
    const filtered = recent.filter(p => p.name !== project.name);

    // Add to front and limit to 10
    const updated = [newEntry, ...filtered].slice(0, 10);
    localStorage.setItem(RECENT_PROJECTS_KEY, JSON.stringify(updated));
    return true;
  } catch (err) {
    return false;
  }
};

export const getRecentProjects = () => {
  try {
    const data = localStorage.getItem(RECENT_PROJECTS_KEY);
    return data ? JSON.parse(data) : [];
  } catch (err) {
    return [];
  }
};

export const exportAsPDF = (svgElement, filename, scale = 2) => {
  return new Promise((resolve, reject) => {
    const clone = svgElement.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    // Get dimensions
    const bbox = svgElement.getBoundingClientRect();
    const width = bbox.width * scale;
    const height = bbox.height * scale;

    clone.setAttribute('width', width);
    clone.setAttribute('height', height);

    // Add white background
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%');
    bg.setAttribute('height', '100%');
    bg.setAttribute('fill', '#ffffff');
    clone.insertBefore(bg, clone.firstChild);

    const svgData = new XMLSerializer().serializeToString(clone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      // Create canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Get image data URL
      const imgData = canvas.toDataURL('image/png');

      // Create PDF using a simple approach (single page with image)
      // For production, you'd use a library like jsPDF
      // This creates a printable HTML that opens in a new window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${filename} - Flowise</title>
              <style>
                @media print {
                  @page {
                    size: ${width > height ? 'landscape' : 'portrait'};
                    margin: 0.5in;
                  }
                  body { margin: 0; }
                }
                body {
                  font-family: Arial, sans-serif;
                  text-align: center;
                  padding: 20px;
                }
                img {
                  max-width: 100%;
                  height: auto;
                }
                h1 {
                  color: #333;
                  font-size: 24px;
                  margin-bottom: 20px;
                }
                .footer {
                  margin-top: 20px;
                  color: #666;
                  font-size: 12px;
                }
              </style>
            </head>
            <body>
              <h1>${filename}</h1>
              <img src="${imgData}" alt="${filename}" />
              <div class="footer">
                Created with Flowise • ${new Date().toLocaleDateString()}
              </div>
              <script>
                window.onload = function() {
                  window.print();
                }
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
        resolve();
      } else {
        reject(new Error('Could not open print window. Please allow popups.'));
      }

      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to export PDF'));
    };

    img.src = url;
  });
};
